import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { catchError, tap } from 'rxjs/operators';
import { forkJoin, of, throwError } from 'rxjs';

import { SessionState } from '@casb/session';
import { NotificationsActions } from './notifications.actions';
import {
  ChannelState,
  NotificationInstance,
  NotificationPatchChannelPayload,
  NotificationPatchPayload,
  NotificationPatchResponse,
  NotificationRowDefinition,
  NotificationSetting,
  NotificationTableRow,
  NotificationsCatalogResponse,
  NotificationsSummaryResponse,
} from '../src/notifications.model';
import { ColumnConfig, NAME_COLUMN, CHANNEL_COLUMN_WIDTH } from '../src/notifications-table.config';

const CATALOG_PATH = 'api/admin/v1/spanva/notification_settings/catalog/';
const SUMMARY_PATH = 'api/admin/v1/spanva/notification_settings/summary/';
const INSTANCE_PATH = (spanvaId: string) =>
  `api/admin/v1/spanva/notification_settings/${spanvaId}/`;

export interface NotificationsStateModel {
  loading: boolean;
  savePending: boolean;
  catalog: NotificationsCatalogResponse | null;
  instances: NotificationInstance[];
}

@State<NotificationsStateModel>({
  name: 'notifications',
  defaults: {
    loading: false,
    savePending: false,
    catalog: null,
    instances: [],
  },
})
@Injectable()
export class NotificationsState {
  private readonly httpClient = inject(HttpClient);
  private readonly store = inject(Store);

  /** Prepends the tenant ID from the session to a relative API path. */
  private tenantUrl(path: string): string {
    const session = this.store.selectSnapshot(SessionState.getSession);
    const tenantId = session?.getTenantId() ?? '';
    return `/${tenantId}/${path}`;
  }

  // ─── Selectors ──────────────────────────────────────────────────────────────

  @Selector()
  static loading(state: NotificationsStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static savePending(state: NotificationsStateModel): boolean {
    return state.savePending;
  }

  @Selector()
  static rowDefinitions(state: NotificationsStateModel): NotificationRowDefinition[] {
    return state.catalog?.notification_rows ?? [];
  }

  /**
   * Derives the dynamic column list from `notification_rows` in the catalog.
   * When categories + per-row `category_id` / `display_order` are present, sorts by
   * category order then display order; otherwise preserves the API row order.
   * The "Name" column is always first and fixed.
   */
  @Selector()
  static columns(state: NotificationsStateModel): ColumnConfig[] {
    const rows = state.catalog?.notification_rows ?? [];
    const categories = state.catalog?.notification_categories ?? [];
    const hasRichMetadata = categories.length > 0 && rows.some((r) => r.category_id != null);

    const sortedRows = hasRichMetadata
      ? rows.slice().sort((a, b) => {
          const categorySortOrder: Record<string, number> = {};
          for (const cat of categories) {
            categorySortOrder[cat.category_id] = cat.sort_order;
          }
          const catDiff =
            (categorySortOrder[a.category_id!] ?? 0) - (categorySortOrder[b.category_id!] ?? 0);
          if (catDiff !== 0) return catDiff;
          return (a.display_order ?? 0) - (b.display_order ?? 0);
        })
      : rows.slice();

    const rowColumns: ColumnConfig[] = sortedRows.map((row) => ({
      id: row.ui_id,
      header: row.title,
      field: row.ui_id,
      filtername: row.ui_id,
      resizable: true,
      sortable: true,
      visible: true,
      width: CHANNEL_COLUMN_WIDTH,
      is_default: true,
    }));

    return [NAME_COLUMN, ...rowColumns];
  }

  /**
   * Transforms each instance into a `NotificationTableRow` where channel states
   * are keyed by `ui_id` for direct lookup in the table cells.
   */
  @Selector()
  static tableRows(state: NotificationsStateModel): NotificationTableRow[] {
    const rowDefs = state.catalog?.notification_rows ?? [];

    return state.instances.map((instance) => {
      const channelsByUiId: Record<string, ChannelState | undefined> = {};
      for (const rowDef of rowDefs) {
        const key = NotificationsState.resolveChannelKey(rowDef, instance);
        channelsByUiId[rowDef.ui_id] = key ? instance.channels[key] : undefined;
      }

      return {
        spanva_id: instance.spanva_id,
        name: instance.agent_name,
        master_enabled: instance.master_enabled,
        recipients: instance.recipients ?? [],
        channelsByUiId,
      };
    });
  }

  // ─── Actions ────────────────────────────────────────────────────────────────

  /**
   * Loads both the catalog schema and all instance data from the single
   * `summary` endpoint, then falls back to the `catalog` endpoint for schema
   * if summary doesn't include it.
   */
  @Action(NotificationsActions.LoadSummary)
  loadSummary(ctx: StateContext<NotificationsStateModel>) {
    ctx.patchState({ loading: true });

    return forkJoin({
      catalog: this.httpClient
        .get<NotificationsCatalogResponse>(this.tenantUrl(CATALOG_PATH))
        .pipe(catchError(() => of(null))),
      summary: this.httpClient
        .get<NotificationsSummaryResponse>(this.tenantUrl(SUMMARY_PATH))
        .pipe(catchError(() => of(null))),
    }).pipe(
      tap(({ catalog, summary }) => {
        const anyFailed = catalog === null || summary === null;
        const categories =
          catalog?.notification_categories ?? summary?.notification_categories ?? [];
        const rows = catalog?.notification_rows ?? summary?.notification_rows ?? [];
        const schemaVersion = catalog?.schema_version ?? summary?.schema_version ?? 1;

        ctx.patchState({
          loading: false,
          catalog: {
            schema_version: schemaVersion,
            notification_categories: categories,
            notification_rows: rows,
          },
          instances: summary?.instances ?? [],
        });

        if (anyFailed) {
          ctx.patchState({
            catalog: {
              schema_version: 1,
              notification_categories: [],
              notification_rows: [],
            },
            instances: [],
          });
          throw new Error('Failed to load notification settings.');
        }
      }),
    );
  }

  @Action(NotificationsActions.SaveInstance)
  saveInstance(
    ctx: StateContext<NotificationsStateModel>,
    action: NotificationsActions.SaveInstance,
  ) {
    ctx.patchState({ savePending: true });

    return this.httpClient
      .patch<NotificationPatchResponse>(
        this.tenantUrl(INSTANCE_PATH(action.spanvaId)),
        action.payload,
      )
      .pipe(
        tap((response) => {
          if (response.error) {
            throw new Error(response.error);
          }
          const updated = response as NotificationInstance;
          if (updated?.spanva_id) {
            const instances = ctx
              .getState()
              .instances.map((inst) => (inst.spanva_id === action.spanvaId ? updated : inst));
            ctx.patchState({ instances, savePending: false });
          } else {
            ctx.patchState({ savePending: false });
          }
        }),
        catchError((err) => {
          ctx.patchState({ savePending: false });
          return throwError(() => err);
        }),
      );
  }

  // ─── Static helpers (used by component) ─────────────────────────────────────

  /**
   * Builds the ordered `NotificationSetting[]` for a given instance's flyout.
   * Called by the component rather than a selector because it requires both
   * catalog row definitions and a specific instance.
   */
  static buildFlyoutSettings(
    rowDefs: NotificationRowDefinition[],
    instance: NotificationInstance,
  ): NotificationSetting[] {
    return rowDefs.map((def) => {
      const key = NotificationsState.resolveChannelKey(def, instance);
      const ch = key ? instance.channels[key] : undefined;
      const rawFrequency = ch?.frequency ?? def.allowed_frequencies[0] ?? '';
      const frequency = def.allowed_frequencies.includes(rawFrequency)
        ? rawFrequency
        : (def.allowed_frequencies[0] ?? '');
      return {
        ui_id: def.ui_id,
        label: def.title,
        frequency_mode: def.frequency_mode,
        allowed_frequencies: def.allowed_frequencies,
        enabled: ch?.enabled ?? false,
        frequency,
      };
    });
  }

  /**
   * Converts flyout `NotificationSetting[]` into the PATCH `channels` payload.
   * Uses `patch_shorthand` when available, otherwise falls back to `channel_key`.
   * Omits `frequency` when the channel is being disabled (server accepts partial).
   */
  static buildPatchPayload(
    rowDefs: NotificationRowDefinition[],
    settings: NotificationSetting[],
    recipients: string[],
    masterEnabled: boolean,
  ): NotificationPatchPayload {
    const channels: Record<string, NotificationPatchChannelPayload> = {};

    for (const setting of settings) {
      const def = rowDefs.find((d) => d.ui_id === setting.ui_id);
      if (!def) continue;

      const patchKeys = NotificationsState.patchKeysForRow(def);
      if (!patchKeys.length) continue;

      const payload: NotificationPatchChannelPayload = setting.enabled
        ? { enabled: true, frequency: setting.frequency }
        : { enabled: false };
      for (const k of patchKeys) {
        channels[k] = payload;
      }
    }

    return { enable_all: masterEnabled, recipients, channels };
  }

  // ─── Private helpers ─────────────────────────────────────────────────────────

  /**
   * Backend channel key(s) to include in a PATCH for this catalog row.
   * `patch_shorthand` wins when set (single key); otherwise all `channel_keys`
   * receive the same enabled/frequency so multi-channel rows stay consistent.
   */
  private static patchKeysForRow(def: NotificationRowDefinition): string[] {
    if (def.patch_shorthand) return [def.patch_shorthand];
    if (def.channel_key) return [def.channel_key];
    if (def.channel_keys?.length) return [...def.channel_keys];
    return [];
  }

  /**
   * Resolves which backend channel key to use for a given row definition against
   * a live instance.  Priority: `channel_key` → first matching `channel_keys` →
   * `patch_shorthand`.
   */
  private static resolveChannelKey(
    def: NotificationRowDefinition,
    instance: NotificationInstance,
  ): string | undefined {
    if (def.channel_key && instance.channels[def.channel_key] !== undefined) {
      return def.channel_key;
    }
    if (def.channel_keys) {
      const match = def.channel_keys.find((k) => instance.channels[k] !== undefined);
      if (match) return match;
    }
    if (def.patch_shorthand && instance.channels[def.patch_shorthand] !== undefined) {
      return def.patch_shorthand;
    }
    return undefined;
  }
}
